import { css } from '@emotion/core'
import { colors, measures, spacing } from 'styles/variables'
import * as MQ from 'styles/media-queries'

const { unit } = measures

// Mobile header styles (Detail/Navigation pattern)
export const mobileHeader = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10000;
  background-color: #000000;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  ${MQ.NOT_TABLETS} {
    display: none;
  }
`

export const headerContainer = css`
  display: flex;
  align-items: center;
  padding: ${spacing(4)} ${spacing(2)};
  position: relative;
`

export const backArrow = css`
  color: #ffffff;
  font-size: 22px;
  cursor: pointer;
  user-select: none;
  position: absolute;
  left: ${spacing(2)};
  top: 50%;
  transform: translateY(-50%);
  
  &:hover {
    opacity: 0.8;
  }
`

export const titleContainer = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`

export const headerTitle = css`
  color: ${colors.white100};
  margin: 0;
  font-weight: 600;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

export const contentWrapper = css`
  ${MQ.TABLETS} {
    padding-top: 50px; /* Reduced space for tighter spacing with header */
  }
  position: relative;
  z-index: 1;
`

export const panel = css`
  height: 80vh;
  position: relative;
  z-index: 1;
  
  ${MQ.TABLETS} {
    height: 100vh;
    background-color: ${colors.black100};
    padding-bottom: 100px; /* Space for bottom navigation */
  }
`

export const title = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  ${MQ.TABLETS} {
    display: none; /* Hide desktop title on mobile - use header instead */
  }
`

export const pageWrapper = css`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
  padding: ${spacing(4)};
  position: relative;
  z-index: 1;
`

export const formContent = css`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`

export const row = css`
  min-height: ${unit * 8}px;
  position: relative;
  z-index: 1;
  
  & label {
    width: 100%;
    display: block;
    margin-bottom: ${spacing(0.75)};
    font-weight: 300;
    margin-left: ${spacing(2)};
  }
  
  /* Make all regular FormInput components 40px height */
  & input[type="text"],
  & input[type="email"],
  & input[type="tel"],
  & input {
    height: 40px !important;
    min-height: 40px !important;
    max-height: 40px !important;
    padding: 0 ${spacing(2)} !important;
    box-sizing: border-box !important;
    font-size: 13px !important;
  }
  
  /* Target FormInput component containers */
  & > div:last-child,
  & > div:not(:first-child) {
    & > div {
      height: 40px !important;
      min-height: 40px !important;
      max-height: 40px !important;
      
      & input {
        height: 40px !important;
        min-height: 40px !important;
        max-height: 40px !important;
        padding: 0 ${spacing(2)} !important;
        box-sizing: border-box !important;
        font-size: 13px !important;
      }
    }
  }
`

export const saveButton = css`
  width: 100%;
  height: 36px;
`

export const buttonText = css`
  color: #000000 !important;
  font-weight: 700;
  font-size: 14px;
`

export const countryInputContainer = css`
  position: relative;
  margin-bottom: ${spacing(6)};
  z-index: 100;
  
  /* Use transform to scale up the dropdown */
  & > div > div > div {
    transform: scaleY(0.9) !important;
    transform-origin: top !important;
    margin-bottom: ${spacing(1.5)};
  }
  
  /* Override placeholder styles for oldInput component */
  & .css-1wa3eu0-placeholder {
    font-size: 14px !important;
    color: ${colors.gray2 || '#666'} !important;
  }
  
  & div[class*="placeholder"] {
    font-size: 14px !important;
    color: ${colors.gray2 || '#666'} !important;
  }
`

export const inputWrapper = css`
  position: relative;
  width: 100%;
  max-width: 100%;
  
  /* Style the FormInput component */
  & > div {
    position: relative;
    width: 100%;
    max-width: 100%;
    
    & input {
      width: 100% !important;
      max-width: 100% !important;
      background-color: transparent !important;
      border: none !important;
      border-radius: 12px !important;
      color: ${colors.black || '#000'} !important;
      font-size: 13px !important;
      height: 32px !important;
      box-sizing: border-box !important;
      
      &::placeholder {
        color: ${colors.gray2 || '#666'} !important;
        font-size: 13px !important;
      }
      
      &:focus {
        outline: none !important;
      }
    }
    
    /* Style async-select component with higher specificity */
    & div[class*="react-select"] .react-select__control,
    & .react-select__control,
    & > div > div[class*="control"] {
      background-color: transparent !important;
      border: 1px solid ${colors.gray7 || '#ccc'} !important;
      border-radius: 12px !important;
      height: 32px !important;
      min-height: 32px !important;
      max-height: 32px !important;
      box-shadow: none !important;
      width: 100% !important;
      min-width: auto !important;
      max-width: 100% !important;
      padding: 0 !important;
      
      &:hover {
        border-color: ${colors.gray7 || '#ccc'} !important;
      }
      
      &.react-select__control--is-focused {
        border-color: ${colors.green6 || '#28a745'} !important;
        box-shadow: none !important;
      }
    }
    
    & div[class*="react-select"] .react-select__value-container,
    & .react-select__value-container,
    & > div > div[class*="ValueContainer"] {
      padding: 0 ${spacing(4)} !important;
      height: 32px !important;
      min-height: 32px !important;
      max-height: 32px !important;
      align-items: center !important;
      display: flex !important;
    }
    
    & .react-select__single-value {
      color: ${colors.black || '#000'} !important;
      font-size: 13px !important;
    }
    
    & .react-select__placeholder {
      color: ${colors.gray2 || '#666'} !important;
      font-size: 14px !important;
    }
    
    & .react-select__input {
      color: ${colors.black || '#000'} !important;
      font-size: 13px !important;
    }
    
    & .react-select__menu {
      background-color: white !important;
      border: 1px solid ${colors.gray7 || '#ccc'} !important;
      border-radius: 12px !important;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
      width: 100% !important;
      min-width: 100% !important;
    }
    
    & .react-select__menu-list {
      width: 100% !important;
      max-width: 100% !important;
      padding: 0 !important;
    }
    
    & .react-select__option {
      background-color: transparent !important;
      color: ${colors.black || '#000'} !important;
      font-size: 13px !important;
      padding: ${spacing(2)} ${spacing(4)} !important;
      
      &:hover {
        background-color: ${colors.gray5 || '#f8f9fa'} !important;
      }
      
      &.react-select__option--is-selected {
        background-color: ${colors.green6 || '#28a745'} !important;
        color: white !important;
      }
    }
    
    & .react-select__dropdown-indicator {
      color: ${colors.gray2 || '#666'} !important;
      padding: 0 ${spacing(2)} !important;
      
      svg {
        width: 16px !important;
        height: 16px !important;
      }
    }
    
    & .react-select__loading-indicator {
      color: ${colors.green2 || '#28a745'} !important;
    }
    
    /* Hide the default label if present */
    & label {
      display: none !important;
    }
  }
  
  /* Force height on all possible containers */
  & > div,
  & > div > div,
  & > div > div > div {
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
  }
  
  /* Ensure NewInput component containers don't interfere with AsyncSelect width */
  & > div > div {
    width: 100% !important;
    max-width: 100% !important;
    min-width: auto !important;
    display: block !important;
  }
`

/* Global styles for portal-rendered React Select menus */
export const portalMenuStyles = css`
  /* When dropdown is rendered in body portal */
  body .react-select__menu-portal {
    z-index: 9999 !important;
  }
  
  body .react-select__menu {
    z-index: 9999 !important;
    background-color: white !important;
    border: 1px solid ${colors.gray7 || '#ccc'} !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
  }
  
  body .react-select__menu-list {
    padding: 0 !important;
  }
  
  body .react-select__option {
    background-color: transparent !important;
    color: ${colors.black || '#000'} !important;
    font-size: 13px !important;
    padding: ${spacing(2)} ${spacing(4)} !important;
    
    &:hover {
      background-color: ${colors.gray5 || '#f8f9fa'} !important;
    }
    
    &.react-select__option--is-selected {
      background-color: ${colors.green6 || '#28a745'} !important;
      color: white !important;
    }
  }
` 